import {
  createRouter,
  createWebHistory,
  createMemoryHistory
} from "vue-router";
import {
  getBrowserLocale,
  DEFAULT_LOCALE,
  SUPPORT_LOCALES,
  i18n,
  loadLocaleMessages,
  setI18nLanguage
} from "../i18n";
import { CHANNELS, DESCRIPTIONS } from "@/utils/constants";

const routes = [];

const childRoutes = [
  // {
  //   path: '',
  //   name: 'Home',
  //   component: () => import(/* webpackChunkName: "home" */ '../views/Home.vue')
  // },
  {
    path: "staff-team",
    name: "StaffPage",
    component: () => import("@/views/StaffPage.vue")
  },
  {
    path: "live-stream",
    name: "LiveStream",
    component: () => import("@/components/VideoPlayerLive.vue")
  },
  {
    path: "404",
    name: "404",
    component: () => import("@/views/404.vue")
  },
  {
    path: "search",
    name: "Search",
    props: true,
    component: () => import("@/views/Search.vue")
  },
  {
    path: "search/:query",
    props: true,
    name: "SearchQuery",
    component: () => import("@/views/SearchQuery.vue")
  },
  {
    path: "detail/:slug",
    name: "NewsDetail",
    props: true,
    component: () => import("@/views/NewsDetail.vue")
  },
  {
    path: 'authorization/',
    name: 'Authorization',
    props: true,
    component: () => import('@/views/Authorization.vue'),
    children: [
      {
        path: '',
        name: 'LogIn_Authorization',
        component: () => import("@/components/authorization/LogIn.vue")
      },
      {
        path: 'registration',
        name: 'Registration_Authorization',
        component: () => import("@/components/authorization/Registration.vue")
      },
      {
        path: 'recovery',
        name: 'Recovery_Authorization',
        component: () => import("@/components/authorization/recoveryPassword/Email.vue")
      },
      {
        path: 'reset_password',
        name: 'ResetePassword_Authorization',
        component: () => import("@/components/authorization/recoveryPassword/ResetePassword.vue")
      },
    ]
  },
  {
    path: 'staff',
    name: 'StaffDetail',
    meta: {
      ssr: false
    },
    component: () => import("@/views/StaffDetail.vue"),
    children: [
      {
        path: 'account-settings',
        name: 'AccountSettings_StaffDetail',
        meta: {
          ssr: false
        },
        component: () => import('@/components/staff/AccountSettings.vue'),
      },
      {
        path: 'articles/:status',
        name: 'Articles_StaffDetail',
        meta: {
          ssr: false
        },
        component: () => import('@/components/staff/Articles.vue'),
      },
      {
        path: '',
        name: 'PreferredLang_StaffDetail',
        meta: {
          ssr: false
        },
        component: () => import('@/components/staff/PreferredLang.vue'),
      },
      {
        path: ':lang',
        name: 'CreateArticles_StaffDetail',
        meta: {
          ssr: false
        },
        component: () => import('@/components/staff/CreateArticle.vue'),
      },
      {
        path: ':lang/edit/:id',
        name: 'EditArticle_StaffDetail',
        meta: {
          ssr: false
        },
        component: () => import('@/components/staff/CreateArticle.vue'),
      },
      {
        path: 'admin',
        name: 'Admin_StaffDetail',
        meta: {
          ssr: false
        },
        component: () => import('@/components/staff/author/admin.vue'),
      },
    ]
  },
  {
    path: "",
    name: "FeedWrapper",
    props: true,
    component: () => import("@/views/MainWrapper.vue")
  },
  {
    path: ":category",
    name: "MainWrapper",
    props: true,
    component: () => import("@/views/MainWrapper.vue")
  }
];

SUPPORT_LOCALES.forEach((locale) => {
  const children = []
  childRoutes.forEach((child) => {
    const recursiveChildren = []
    if (child.children) {
      child.children.forEach((recChild) => {
        recursiveChildren.push({
          ...recChild,
          name: `${locale}_${recChild.name}`
        })
      })
    }
    children.push({
      ...child,
      name: `${locale}_${child.name}`,
      children: recursiveChildren
    })
  })
  locale = locale === DEFAULT_LOCALE ? '' : locale
  routes.unshift({
    path: `/${locale}`,
    component: {
      template: '<router-view></router-view>'
    },
    children
  })
})

const isServer = typeof window === "undefined";
const history = isServer ? createMemoryHistory() : createWebHistory();

const beforeEach = async (to, from, next) => {
  if (!isServer) {
    if (
      to.name?.substr(to.name?.length - 10) === "NewsDetail" &&
      from.name?.substr(from.name?.length - 10) !== "NewsDetail"
    ) {
      window?.scrollTo({
        top: 0
      });
    } else {
      window?.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }
  let paramsLocale;
  // for (const locale of SUPPORT_LOCALES) {
  //   const regex = new RegExp(
  //     `/${locale}/${locale.length + 1 === to.path.length ? `|/${locale}` : ""}`
  //   );
  //   let m;
  //   if ((m = regex.exec(to.path)) !== null) {
  //     paramsLocale = m[0].slice(1, locale.length + 1);
  //     break
  //   }
  // }
  SUPPORT_LOCALES.forEach(locale => {
    const regex = new RegExp(
      `/${locale}/${locale.length + 1 === to.path.length ? `|/${locale}` : ""}`
    );
    // console.log(`/${locale}/${locale.length + 1 === to.path.length ? `|/${locale}` : ""}`)
    let m;
    if ((m = regex.exec(to.path)) !== null) {
      paramsLocale = m[0].slice(1, locale.length + 1);
    }
  });

  paramsLocale = paramsLocale === undefined ? DEFAULT_LOCALE : paramsLocale;

  const newLocale = getBrowserLocale();

  // use locale if paramsLocale is not in SUPPORT_LOCALES
  if (!SUPPORT_LOCALES.includes(paramsLocale)) {
    return next({
      name: `${newLocale}_FeedWrapper`
    });
  }

  // load locale messages
  if (!i18n.global.availableLocales.includes(paramsLocale)) {
    await loadLocaleMessages(i18n, paramsLocale);
  }

  // set i18n language
  setI18nLanguage(i18n, paramsLocale);

  if (!isServer) {
    // default SEO
    const routeName = to.name.split("_")[1];
    const subtitle = i18n.global.t(`routes.${routeName}`);
    document.title = subtitle;
    document
      .querySelector('meta[name="og:site_name"]')
      .setAttribute("content", subtitle);
    document
      .querySelector('meta[name="og:title"]')
      .setAttribute("content", subtitle);
    document
      .querySelector('meta[name="og:type"]')
      .setAttribute(
        "content",
        routeName === "NewsDetail" ? "article" : "website"
      );
    document
      .querySelector('meta[name="telegram:channel"]')
      .setAttribute("content", `@${CHANNELS[paramsLocale]}`);
    document
      .querySelector('meta[name="telegram:author_url"]')
      .setAttribute("content", `https://t.me/${CHANNELS[paramsLocale]}`);
    document
      .querySelector('meta[name="og:description"]')
      .setAttribute("content", DESCRIPTIONS[paramsLocale]);
    document
      .querySelector('meta[name="description"]')
      .setAttribute(
        "content",
        DESCRIPTIONS["uz"] +
          DESCRIPTIONS["ru"] +
          DESCRIPTIONS["en"] +
          DESCRIPTIONS["uz-to"]
      );
    document
      .querySelector('meta[name="og:url"]')
      .setAttribute(
        "content",
        i18n.global.locale.value === "uz"
          ? `https://zamon.uz`
          : `https://zamon.uz/${i18n.global.locale.value}`
      );
    document
      .querySelector('meta[name="og:image"]')
      .setAttribute("content", "/logo.png");
    document
      .querySelector('meta[name="article:published_time"]')
      .setAttribute("content", "19:00");
    document
      .querySelector('meta[name="author"]')
      .setAttribute("content", "Zamon");
  }

  return next();
};

export default function() {
  const router = createRouter({
    history,
    // history: createMemoryHistory(process.env.BASE_URL),
    routes,
    scrollBehavior() {
      return {
        x: 0,
        y: 0
      };
    }
  });

  router.beforeEach(beforeEach);
  return router;
}
